/**
 * THE FOUR INVISIBLE SETS, COMPUTED — pure. No React, no I/O, no writes.
 *
 * Brian: "Anything hidden, I need it deployed because hidden doesn't get
 * worked." These four sets were CSVs, an HTML file and a scratch JSON on one
 * laptop. This module is the arithmetic behind the surfaces that replace them.
 *
 * ⚠️ FILTER ON THE ATTRIBUTE ASKED FOR, NEVER ON THE FIELD THAT IS BLANK.
 * Every function here returns `shown + flagged === all`, and the surface
 * renders the reconciliation. A record with a missing body still appears — it
 * appears FLAGGED. Dropping it from the list is how 3,162 records became "274"
 * in one direction and invisible in the other.
 *
 * ⚠️ A BLANK FIELD IS NOT AN ABSENT BODY. `sr_governing_body` being empty says
 * nothing about whether the body is knowable: DoDEA Pacific sat at a false zero
 * while holding records that named it only in `sr_conference`. So every
 * attribution here probes FOUR fields at once — see PROBE_FIELDS — and the
 * genuinely-invisible set is the records where all four are silent. Measured
 * 2026-10-11 by the governing-body lane over 15,274 live records: that is the
 * difference between 3,162 records with an empty body field and 274 that are
 * genuinely invisible, and it is the single most important line in this file.
 *
 * ⚠️ WHAT THIS MODULE CANNOT DO, STATED RATHER THAN PAPERED OVER. The registry
 * lane's prober matched by ALIAS with a JURISDICTION GUARD, and
 * `build_registry.py` drops the alias table from its output. So the live
 * re-check here is an EXACT, case-folded label match, which is strictly weaker.
 * It is used in one direction only: it can show that a body carded ABSENT now
 * holds records, and it NEVER asserts that a body is absent. A weaker probe
 * answering "absent" is precisely the false zero the registry exists to kill —
 * at roughly a 7% rate against official names. See `liveRecheck()`.
 *
 * ⚠️ NEVER KEY A BODY ON AN ABBREVIATION ALONE. `matchesLabel` compares whole
 * normalized strings. It does not substring-match and it does not match codes
 * against free text, because a loose "state word anywhere in the record"
 * fallback once returned CHSAA = 134 when New York holds exactly 6 — Colorado
 * answering for New York. MHSAA is live at 4 labels across 3 jurisdictions.
 */

/** The four fields the registry lane probes together, in its own order. */
export const PROBE_FIELDS = ['sr_governing_body', 'sr_conference', 'sr_championship_name', 'sr_region']

/** The value the object uses for "somebody filled this in with nothing". */
export const UNSPECIFIED = 'Unspecified'

/** Cap on records carried per group, so one 258-row label cannot bloat a render. */
export const SAMPLE_CAP = 200

const S = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim())

/** Populated means populated. `Unspecified` was typed by a person and means nothing. */
const populated = (v) => {
  const s = S(v)
  return s && s !== UNSPECIFIED ? s : ''
}

/** Whole-string, case-folded, whitespace-collapsed. Never a substring. */
export const normLabel = (v) => S(v).replace(/\s+/g, ' ').toLowerCase()

export const matchesLabel = (a, b) => {
  const x = normLabel(a)
  return !!x && x === normLabel(b)
}

const raw = (c) => c?.raw || c || {}

/* ---------------------------------------------------------------------------
 * SURFACE 2 — the records with no governing body, each with the REASON.
 *
 * Grouped by the label that would settle them, so a whole label can be cleared
 * in ONE decision rather than record by record. Brian: "`NCCAA` 106,
 * `Landmark Conference` 23, bare `NJSIAA` 21, `3C2A` 14."
 *
 * The reason is the output, not a footnote. Four of them, and they take
 * different work from different people:
 *
 *   MINT    a conference label is present and the registry holds no body under
 *           it. Minting a new body label is BRIAN'S CALL, not a fill.
 *   MAP     a conference label is present AND exact-matches a registry body.
 *           This one is a fill: the body is known, the field is just empty.
 *   NAMED   no conference, but a REGION is recorded. A region is a label, so
 *           it can be cleared in one pass like any other label.
 *   SILENT  no body, no conference, no region. GENUINELY INVISIBLE: the only
 *           thing left is the championship title, and a title is PROSE, not a
 *           label. "NCCAA Men's Soccer Championship" reads like an attribution
 *           and is not one — matching an authority out of it needs the alias
 *           table, and substring-matching it is the loose fallback that let
 *           Colorado's CHSAA answer for New York's. So a record whose only
 *           signal is its own name counts as silent here rather than as
 *           attributed, because claiming otherwise is the reassuring error.
 * ------------------------------------------------------------------------- */
export const REASONS = {
  MINT: 'MINT',
  MAP: 'MAP',
  NAMED: 'NAMED',
  UNSPEC: 'UNSPEC',
  SILENT: 'SILENT',
}

export const REASON_COPY = {
  MINT: {
    label: 'Needs a new body label minted',
    who: 'Brian — this is a decision, not a fill',
    why: 'A conference is recorded and the registry holds no body under that label. '
      + 'Creating one is a judgement about what the organisation IS; an automation '
      + 'guessing it would put a wrong authority on a real record.',
  },
  MAP: {
    label: 'Maps to a body the registry already holds',
    who: 'the governing-body fill lane',
    why: 'The conference label matches a registry body exactly. The body is known '
      + 'and the field is simply empty, so this is a fill rather than a decision.',
  },
  NAMED: {
    label: 'A region is recorded, no conference',
    who: 'whoever works the region',
    why: 'No conference, but a region is on the record. A region is a label, so these '
      + 'clear a whole group at a time the same way a conference does.',
  },
  UNSPEC: {
    label: 'Somebody typed “Unspecified”',
    who: 'the data, not the board',
    why: 'A person opened the conference field and entered a word that names no '
      + 'organisation. Kept apart from an empty field because one was typed and the '
      + 'other never was — the same split Body Coverage makes, for the same reason: '
      + 'they need different conversations with different people. No registry entry '
      + 'can ever help one of these.',
  },
  SILENT: {
    label: 'Genuinely invisible — no body, no conference, no region',
    who: 'needs sourcing from outside the object',
    why: 'The only thing left on the record is its own title, and a title is prose. '
      + '“NCCAA Men’s Soccer Championship” reads like an attribution and is not one: '
      + 'pulling an authority out of it needs the alias table, and substring-matching '
      + 'it is the loose fallback that let Colorado answer for New York. So these count '
      + 'as silent, which is the loud direction to be wrong in.',
  },
}

/**
 * @param records normalized championships (each carrying `.raw`)
 * @param registryBodies the registry snapshot's `bodies` array
 */
export function unfilledBodies(records, registryBodies) {
  const all = records || []
  const bodies = registryBodies || []

  /* One index, built once. Both the official name and the code are keys, because
   * the object carries both shapes as labels — but each is matched WHOLE. */
  const index = new Map()
  for (const b of bodies) {
    const n = normLabel(b.name)
    if (n) index.set(n, b)
    const c = normLabel(b.code)
    if (c) index.set(c, b)
  }

  const groups = new Map()
  let withBody = 0
  const counts = { MINT: 0, MAP: 0, NAMED: 0, UNSPEC: 0, SILENT: 0 }

  for (const c of all) {
    const r = raw(c)
    if (populated(r.sr_governing_body)) { withBody++; continue }

    const conf = populated(r.sr_conference)
    const region = populated(r.sr_region)

    let reason
    let key
    if (conf) {
      reason = index.has(normLabel(conf)) ? REASONS.MAP : REASONS.MINT
      key = conf
    } else if (region) {
      reason = REASONS.NAMED
      key = region
    } else if (S(r.sr_conference) === UNSPECIFIED) {
      reason = REASONS.UNSPEC
      key = UNSPECIFIED
    } else {
      /* Grouped by SEGMENT, because that is the only label left and a segment is
       * still a decision somebody can take in one pass. Not by championship
       * name: that would make 274 groups of one and nothing could be cleared. */
      reason = REASONS.SILENT
      key = populated(r.sr_segment) || '(no segment either)'
    }

    counts[reason]++
    const gk = `${reason}\u0000${key}`
    if (!groups.has(gk)) {
      groups.set(gk, {
        key: gk, label: key, reason, count: 0,
        card: reason === REASONS.MAP ? index.get(normLabel(key)) || null : null,
        states: new Set(), sports: new Map(), segments: new Map(),
        crowned: 0, records: [],
      })
    }
    const g = groups.get(gk)
    g.count++
    const st = populated(r.sr_state_location) || populated(r.sr_state)
    if (st) g.states.add(st)
    const sp = populated(r.sr_sport) || '(no sport)'
    g.sports.set(sp, (g.sports.get(sp) || 0) + 1)
    const sg = populated(r.sr_segment) || '(no segment)'
    g.segments.set(sg, (g.segments.get(sg) || 0) + 1)
    if (populated(r.current_champion)) g.crowned++
    if (g.records.length < SAMPLE_CAP) g.records.push(c)
  }

  const rows = [...groups.values()]
    .map((g) => ({
      ...g,
      states: [...g.states].sort(),
      sports: [...g.sports.entries()].sort((a, b) => b[1] - a[1]),
      segments: [...g.segments.entries()].sort((a, b) => b[1] - a[1]),
      truncated: g.count > g.records.length,
    }))
    .sort((a, b) => b.count - a.count)

  const unfilled = counts.MINT + counts.MAP + counts.NAMED + counts.UNSPEC + counts.SILENT
  const totals = {
    universe: all.length,
    withBody,
    unfilled,
    ...counts,
    /* The decision set: a conference is recorded and it settles nothing. */
    conferenceDoesNotSettle: counts.MINT + counts.MAP,
    accounted: withBody + unfilled,
  }

  return {
    rows,
    byReason: (reason) => rows.filter((r) => r.reason === reason),
    totals,
    reconciled: totals.accounted === totals.universe,
    shortfall: totals.universe - totals.accounted,
  }
}

/* ---------------------------------------------------------------------------
 * SURFACE 5 — THE RECORDS NOBODY OWNS.
 *
 * Brian, 2026-10-13: "If something is unassigned - i need to know about it. we
 * must be able to assign it."
 *
 * ⚠️ THIS WAS INVISIBLE IN THE EXACT WAY THIS PAGE EXISTS FOR, and the board
 * said so in its own source without fixing it: CommandCenter.jsx carried the
 * comment "THE OWNER FILTER CANNOT EXPRESS 'UNASSIGNED'", because the value
 * that selects an ownerless record is `''` and the filter reads `''` as "no
 * filter at all". So the one cohort a manager has to act on was the one cohort
 * that could not be asked for. Measured live 2026-10-13 off the served
 * snapshot: 2,399 of 15,814.
 *
 * ⚠️ FILTER ON THE ATTRIBUTE ASKED FOR, NEVER ON THE FIELD THAT IS BLANK — and
 * here the attribute asked for IS a blank field, which is the case that breaks
 * naive code. `ownerName()` is the single authority on what counts as nobody:
 * it already folds 'Unassigned', 'Unknown', 'None', 'n/a' and a dash to '', so
 * a record whose rep field literally reads "Unassigned" is counted here rather
 * than passing as owned. Reproducing that test locally is how the two halves
 * drift apart.
 *
 * ⚠️ THE REASON IS THE OUTPUT, and it is derived from what the RECORD carries —
 * never from the assignment map, which lives on the server and is not in this
 * payload. The lane that backfilled 143 owner seats reported 2,318 records its
 * map could not decide, split 1,949 "no rep on the breakdown for this body" /
 * 336 "no governing body on the record" / 33 "retired". Only the second and
 * third of those are visible from a record, so only those two are claimed:
 *
 *   RETIRED    the title is retired. The map skips it BY DESIGN and so does
 *              this surface's gap count — a retired record with no rep is not
 *              a gap, it is a record nobody should be working.
 *   NO_BODY    no governing body and no conference. Nothing can route this to
 *              a rep until the body is settled, which is Surface 2's work.
 *              Assignable by hand today; not decidable by any rule.
 *   OPEN       a body or conference IS recorded, so this is routable now — by
 *              a manager here, or by the server's own map once the body has a
 *              rep on the 2026-2027 breakdown.
 *
 * ⚠️ WHETHER THE SHEET CARRIES A REP FOR THE BODY IS NOT KNOWABLE FROM HERE,
 * so OPEN deliberately does not split into "the map will take it" and "the
 * sheet has no rep for this body". Guessing that split would put a confident
 * wrong reason on 1,949 records, and a wrong reason sends the work to the
 * wrong person — worse than no reason at all.
 * ------------------------------------------------------------------------- */
export const OWNER_REASONS = { RETIRED: 'RETIRED', NO_BODY: 'NO_BODY', OPEN: 'OPEN' }

export const OWNER_REASON_COPY = {
  OPEN: {
    label: 'Routable now — a body is recorded',
    who: 'a manager here, or the server’s assignment map',
    why: 'A governing body or conference is on the record, so there is something to route '
      + 'on. Assign it from the Command Center with the rep dropdown set to '
      + '“No stored CRM rep”; the reason you type goes to the permanent ledger.',
  },
  NO_BODY: {
    label: 'No governing body and no conference',
    who: 'settle the body first — Surface 2',
    /* "to a rep" and not "to a territory": check-ownership-language bans the
     * second word outright, and it is right to — nobody here has one. */
    why: 'Nothing on the record says which organisation sanctions it, so no rule can route '
      + 'it to a rep. A human can still assign it by hand; a machine cannot, and the '
      + 'server’s map records exactly this as “no governing body on the record”.',
  },
  RETIRED: {
    label: 'Retired — not a gap',
    who: 'nobody, deliberately',
    why: 'The title is retired. The server’s assignment map skips retired records by design '
      + 'and this surface keeps them out of the gap count, because counting a dead record as '
      + 'unassigned work is how a queue gets padded with things nobody should touch.',
  },
}

/**
 * @param records normalized championships (each carrying `.raw`)
 * @param ownerOf the authority on "who owns this" — pass `ownerName` from
 *        lib/championships.js. Injected rather than imported so this module
 *        stays pure and the test can prove the two agree.
 */
export function ownerlessRecords(records, ownerOf) {
  const all = records || []
  const owner = typeof ownerOf === 'function' ? ownerOf : (c) => S(raw(c).sr_assigned_rep)

  const groups = new Map()
  const counts = { RETIRED: 0, NO_BODY: 0, OPEN: 0 }
  let owned = 0
  let bornOwnerless = 0

  for (const c of all) {
    if (owner(c)) { owned++; continue }
    const r = raw(c)
    const name = S(r.championship_name) || S(r.sr_championship_name)
    const body = populated(r.governing_body) || populated(r.sr_governing_body)
    const conf = populated(r.sr_conference)

    let reason
    let key
    if (/^retired\b/i.test(name)) {
      reason = OWNER_REASONS.RETIRED
      key = 'RETIRED titles'
    } else if (body || conf) {
      reason = OWNER_REASONS.OPEN
      key = body || conf
    } else {
      reason = OWNER_REASONS.NO_BODY
      key = populated(r.sr_segment) || '(no segment either)'
    }

    counts[reason]++
    /* `sr_gap_no_owner` is the flag the CREATE path sets when a record is born
     * without a decidable owner. Carried alongside the reason, not instead of
     * it: "born blank" and "went blank" are different facts about the same
     * record and they need different people. */
    if (S(r.sr_gap_no_owner) && !/^(false|0|no|off)$/i.test(S(r.sr_gap_no_owner))) bornOwnerless++

    const gk = `${reason}\u0000${key}`
    if (!groups.has(gk)) {
      groups.set(gk, { key: gk, label: key, reason, count: 0, crowned: 0, dated: 0,
        states: new Set(), sports: new Map(), records: [] })
    }
    const g = groups.get(gk)
    g.count++
    if (populated(r.current_champion)) g.crowned++
    if (c?.effDate) g.dated++
    const st = populated(r.sr_state_location) || populated(r.sr_state)
    if (st) g.states.add(st)
    const sp = populated(r.sr_sport) || '(no sport)'
    g.sports.set(sp, (g.sports.get(sp) || 0) + 1)
    if (g.records.length < SAMPLE_CAP) g.records.push(c)
  }

  const rows = [...groups.values()]
    .map((g) => ({
      ...g,
      states: [...g.states].sort(),
      sports: [...g.sports.entries()].sort((a, b) => b[1] - a[1]),
      truncated: g.count > g.records.length,
    }))
    /* CROWNED FIRST, then volume. A concluded-but-unowned title is live revenue
     * with nobody on it — the same value order the server-side backfill ran in
     * ("a concluded-uncrowned title is live revenue"). Alphabetical would bury
     * it. */
    .sort((a, b) => b.crowned - a.crowned || b.count - a.count)

  const ownerless = counts.RETIRED + counts.NO_BODY + counts.OPEN
  const totals = {
    universe: all.length,
    owned,
    ownerless,
    ...counts,
    /* THE NUMBER THAT IS ACTUALLY WORK. Retired records are excluded, because
     * an out-of-scope record is not a gap. */
    gap: counts.NO_BODY + counts.OPEN,
    crowned: rows.reduce((a, r) => a + r.crowned, 0),
    bornOwnerless,
    accounted: owned + ownerless,
  }

  return {
    rows,
    byReason: (reason) => rows.filter((r) => r.reason === reason),
    totals,
    /* shown + flagged === all, rendered on the surface. Every record in the
     * object is in exactly one bucket above. */
    reconciled: totals.accounted === totals.universe,
    shortfall: totals.universe - totals.accounted,
  }
}

/* ---------------------------------------------------------------------------
 * The live re-check, used in ONE direction only.
 *
 * Returns, for a registry body carded ABSENT, the count of live records whose
 * governing-body, conference or region label EXACTLY equals its official name
 * or its code. A non-zero answer is a finding: the snapshot is stale and that
 * body is no longer absent. A zero answer asserts NOTHING — the alias table the
 * real prober uses is not in the snapshot, so zero here is "no exact label",
 * not "absent".
 * ------------------------------------------------------------------------- */
export function liveRecheck(records, bodies) {
  const wanted = new Map() // normalized label -> body code
  for (const b of bodies || []) {
    const n = normLabel(b.name)
    if (n) wanted.set(n, b.code)
    const c = normLabel(b.code)
    if (c) wanted.set(c, b.code)
  }
  const hits = new Map() // code -> count
  for (const c of records || []) {
    const r = raw(c)
    /* sr_championship_name is deliberately NOT probed here. A championship title
     * contains its body's name as ordinary prose ("NCCAA Men's Soccer
     * Championship"), so a whole-string match on it is meaningless and a
     * substring match is the loose fallback that let Colorado answer for New
     * York. Only fields that hold a LABEL are compared. */
    for (const f of ['sr_governing_body', 'sr_conference', 'sr_region']) {
      const code = wanted.get(normLabel(populated(r[f])))
      if (code) { hits.set(code, (hits.get(code) || 0) + 1); break }
    }
  }
  return hits
}

/* ---------------------------------------------------------------------------
 * SURFACE 1 — the creation queue. Bodies the object has NEVER recorded.
 *
 * Rank comes from the registry file; it is not recomputed here. The queue is a
 * CREATION queue, not a repair queue — "present at zero" came back empty, and
 * that was the finding.
 * ------------------------------------------------------------------------- */
export function creationQueue(bodies, records) {
  const all = bodies || []
  const absent = all.filter((b) => b.state === 'ABSENT')
  const recheck = liveRecheck(records || [], absent)

  const rows = absent
    .map((b) => ({ ...b, liveNow: recheck.get(b.code) || 0 }))
    .sort((a, b) => b.yieldHint - a.yieldHint || a.name.localeCompare(b.name))

  const hsPrivate = rows.filter((r) => /Private|Independent/i.test(r.level || ''))
  const stale = rows.filter((r) => r.liveNow > 0)

  /* Texas is the headline Brian named: the state with the most absent bodies. */
  const byJuris = new Map()
  for (const r of hsPrivate) {
    const j = r.juris || '(no jurisdiction)'
    byJuris.set(j, (byJuris.get(j) || 0) + 1)
  }

  return {
    rows,
    hsPrivate,
    hsPrivateByJuris: [...byJuris.entries()].sort((a, b) => b[1] - a[1]),
    stale,
    totals: {
      registry: all.length,
      present: all.filter((b) => b.state === 'PRESENT_WITH_CHAMPIONSHIPS').length,
      absent: rows.length,
      hsPrivate: hsPrivate.length,
      estTitles: rows.reduce((a, r) => a + r.yieldHint, 0),
      staleRows: stale.length,
    },
    /* The registry's own two states must account for every body in it. */
    reconciled: all.length === all.filter((b) => b.state === 'ABSENT').length
      + all.filter((b) => b.state === 'PRESENT_WITH_CHAMPIONSHIPS').length,
  }
}

/* ---------------------------------------------------------------------------
 * SURFACE 3 — titles decided by STANDINGS, not by a final.
 *
 * The hunt asks "has the season ended" and gets these wrong: a points title is
 * clinchable before the last race. It refused the GT4 America Manufacturers
 * title for exactly this reason. These have to be visibly a DIFFERENT KIND of
 * record, which is what `basis` is for.
 * ------------------------------------------------------------------------- */
export function standingsDecided(bodies, records) {
  const all = bodies || []
  const graded = all.filter((b) => b.basis === 'STANDINGS' || b.basis === 'FINAL')
  const standings = all.filter((b) => b.basis === 'STANDINGS')
  const byCode = new Map(standings.map((b) => [b.code, b]))

  /* Live join, same exact-label discipline as liveRecheck — label fields only. */
  const wanted = new Map()
  for (const b of standings) {
    const n = normLabel(b.name)
    if (n) wanted.set(n, b.code)
    const c = normLabel(b.code)
    if (c) wanted.set(c, b.code)
  }
  const live = new Map() // code -> { count, crowned, records }
  for (const c of records || []) {
    const r = raw(c)
    let code = null
    for (const f of ['sr_governing_body', 'sr_conference', 'sr_region']) {
      code = wanted.get(normLabel(populated(r[f]))) || null
      if (code) break
    }
    if (!code) continue
    if (!live.has(code)) live.set(code, { count: 0, crowned: 0, records: [] })
    const e = live.get(code)
    e.count++
    if (populated(r.current_champion)) e.crowned++
    if (e.records.length < SAMPLE_CAP) e.records.push(c)
  }

  const rows = standings
    .map((b) => {
      const e = live.get(b.code) || { count: 0, crowned: 0, records: [] }
      return {
        ...b,
        liveCount: e.count,
        liveCrowned: e.crowned,
        records: e.records,
        truncated: e.count > e.records.length,
      }
    })
    .sort((a, b) => b.liveCount - a.liveCount || b.held - a.held || a.name.localeCompare(b.name))

  const liveTotal = rows.reduce((a, r) => a + r.liveCount, 0)
  const snapshotHeld = standings.reduce((a, b) => a + b.held, 0)

  return {
    rows,
    byCode,
    totals: {
      standingsBodies: standings.length,
      finalBodies: graded.length - standings.length,
      gradedBodies: graded.length,
      ungradedBodies: all.length - graded.length,
      /* Two numbers, both stated, because they disagree and the difference is
       * the alias gap — not a figure to average away. */
      liveExactLabel: liveTotal,
      snapshotAliasProbe: snapshotHeld,
      liveCrowned: rows.reduce((a, r) => a + r.liveCrowned, 0),
      bodiesWithNoLiveMatch: rows.filter((r) => r.liveCount === 0).length,
    },
  }
}

/* ---------------------------------------------------------------------------
 * SURFACE 4 — the registry rows graded KNOWLEDGE, not verified.
 *
 * These must render as unverified ON THEIR FACE. The worst outcome on this page
 * is somebody reading a KNOWLEDGE row as sourced, so there is no mixed list and
 * no mixed table: the grade is the first thing in every row.
 * ------------------------------------------------------------------------- */
export const GRADES = {
  OFFICIAL: {
    label: 'Sourced',
    short: 'OFFICIAL',
    why: "Read off the body's own page in the registry session, with a URL on the row.",
    verified: true,
  },
  'OFFICIAL-COUNT': {
    label: 'Count sourced, names not',
    short: 'OFFICIAL-COUNT',
    why: "The body's own published TOTAL was read. The individual title names were not "
      + 'established, so the count is sourced and the roster is not.',
    verified: false,
  },
  KNOWLEDGE: {
    label: 'UNVERIFIED — asserted, not sourced',
    short: 'KNOWLEDGE',
    why: 'Asserted from model knowledge and NOT verified against the body in the registry '
      + 'session. No URL was read. Treat every field on the row as a lead to check, never '
      + 'as a fact to quote.',
    verified: false,
  },
}

export function registryGrades(bodies) {
  const all = bodies || []
  const byGrade = new Map()
  for (const b of all) {
    const g = b.evidence || '(ungraded)'
    if (!byGrade.has(g)) byGrade.set(g, [])
    byGrade.get(g).push(b)
  }

  const unverified = all.filter((b) => !GRADES[b.evidence]?.verified)
  const knowledge = all.filter((b) => b.evidence === 'KNOWLEDGE')

  /* What is actually at stake: an unverified row that the object already holds
   * records against is a row somebody is ALREADY working off. */
  const knowledgeHeld = knowledge.filter((b) => b.held > 0)

  const byLevel = new Map()
  for (const b of knowledge) {
    const k = b.level || '(no level)'
    if (!byLevel.has(k)) byLevel.set(k, { level: k, count: 0, held: 0 })
    const e = byLevel.get(k)
    e.count++
    e.held += b.held
  }

  return {
    rows: knowledge.slice().sort((a, b) => b.held - a.held || b.yieldHint - a.yieldHint
      || a.name.localeCompare(b.name)),
    byGrade: [...byGrade.entries()]
      .map(([grade, list]) => ({
        grade,
        count: list.length,
        held: list.reduce((a, b) => a + b.held, 0),
        verified: !!GRADES[grade]?.verified,
      }))
      .sort((a, b) => b.count - a.count),
    byLevel: [...byLevel.values()].sort((a, b) => b.count - a.count),
    totals: {
      registry: all.length,
      verified: all.length - unverified.length,
      unverified: unverified.length,
      knowledge: knowledge.length,
      knowledgeHeld: knowledgeHeld.length,
      knowledgeHeldRecords: knowledgeHeld.reduce((a, b) => a + b.held, 0),
      estTitlesBehindKnowledge: knowledge.reduce((a, b) => a + b.yieldHint, 0),
    },
    reconciled: all.length === [...byGrade.values()].reduce((a, l) => a + l.length, 0),
  }
}
